import * as XLSX from 'xlsx';
import { RequirementColumn } from '../../type';
import { Product } from '@/types/product';
import { RequirementAddItemUseRepl } from '@/types/requirement';
import { divided, multi, plus } from '@/utils/calc';
import { RcFile } from 'antd/es/upload';

interface BomHeader {
  model: string;
  name: string;
  code: string;
  version: string;
}

/**
 * 获取单元格值并trim
 */
const getCellValue = (row: any[], index: number): string => {
  if (row[index] === undefined || row[index] === null) return '';
  return String(row[index]).trim();
};

const isProductRow = (row: any[], codeIdx: number, nameIdx: number) => {
  return Array.isArray(row) && row.length > 1 && !!getCellValue(row, codeIdx) && !!getCellValue(row, nameIdx)
}

const isHeaderRow = (row: any[]) => {
  if (!Array.isArray(row)) return false
  let headerStr = row.join('')
  return headerStr.includes("料号") && headerStr.includes("品名")
}

const getRealQuantity = (row: any[], saleQty: number, qtyIdx: number, rateIdx: number) => {
  if (!Array.isArray(row) || row.length === 0) return 0
  const rate = divided(getCellValue(row, rateIdx), "100");
  const itemRate = multi(getCellValue(row, qtyIdx), rate);
  const itemQuantity = plus(getCellValue(row, qtyIdx), itemRate);
  const allQuantity = multi(
    itemQuantity,
    saleQty.toString()
  );
  return Math.ceil(Number(allQuantity));
};

export const RcFile2Buffer = (file: RcFile) => {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    // 监听加载完成事件
    reader.onload = (event) => {
      const result = event.target?.result
      if (result instanceof ArrayBuffer) {
        resolve(result);
      } else {
        reject(new Error('读取结果不是 ArrayBuffer 格式'));
      }
    };

    // 监听错误事件
    reader.onerror = (error) => {
      reject(error);
    };

    // 开始读取文件为 ArrayBuffer
    reader.readAsArrayBuffer(file);
  });
};
type ResReqColType = Omit<RequirementColumn, "reqs"> & { locators?: string, reqs: ResReqColType[] };
export const parseBomFileBuffer = (buffer: ArrayBuffer, saleQty: number, needHeader?: boolean) => {
  // 使用 XLSX 读取 ArrayBuffer
  const workbook = XLSX.read(buffer, { type: 'array' });

  // 获取第一个 sheet 的名称
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];

  // 将 sheet 转换为 JSON 数据
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  //表头行号
  let headerStart: number = jsonData.length;
  //当前的工序
  let curProcess: string = '';
  //序号列索引
  let seqIdx = 0;
  //料号列索引
  let codeIdx = 0;
  //品名列索引
  let nameIdx = 0;
  //规格列索引
  let modelIdx = 0;
  //单位列索引
  let uomIdx = 0;
  //数量列索引
  let qtyIdx = 0;
  //损耗率列索引
  let rateIdx = 0;
  //备注列索引
  let remarkIdx = 0;
  //位置列索引
  let locatorIdx = 0;
  //解析结果
  let resData: ResReqColType[] = [];
  let headerData: BomHeader = {
    name: '',
    model: '',
    code: '',
    version: '',
  };

  for (let i = 0; i < jsonData.length; i++) {
    const row = jsonData[i] as Array<any>;
    if (!!!headerData.name) {
      let rowTemp = row.filter(t => !!t);
      let idx = rowTemp.findIndex(t => (t + '').includes('产品名称'));
      if (idx !== -1) headerData.name = (rowTemp[idx + 1] ?? '') + ''
    }
    if (!!!headerData.model) {
      let rowTemp = row.filter(t => !!t);
      let idx = rowTemp.findIndex(t => (t + '').includes('型号规格'));
      if (idx !== -1) headerData.model = (rowTemp[idx + 1] ?? '') + ''
    }
    if (!!!headerData.code) {
      let rowTemp = row.filter(t => !!t);
      let idx = rowTemp.findIndex(t => (t + '').includes('成品料号'));
      if (idx !== -1) headerData.code = (rowTemp[idx + 1] ?? '') + ''
    }
    if (!!!headerData.version) {
      let rowTemp = row.filter(t => !!t);
      let idx = rowTemp.findIndex(t => (t + '').includes('版本'));
      if (idx !== -1) headerData.version = (rowTemp[idx + 1] ?? '') + ''
    }

    if (i > headerStart) {  //正式数据
      if (isProductRow(row, codeIdx, nameIdx)) {
        resData.push({
          product: {
            id: getCellValue(row, codeIdx),
            code: getCellValue(row, codeIdx),
            name: getCellValue(row, nameIdx),
            model: getCellValue(row, modelIdx),
            uom: getCellValue(row, uomIdx),
            process: curProcess,
            desc: getCellValue(row, remarkIdx),
          } as Product,
          reqs: [],
          useRepl: RequirementAddItemUseRepl.NOT_USE,
          bomItemId: '',
          bomReplId: '',
          bomRequireCount: getRealQuantity(row, saleQty, qtyIdx, rateIdx),
          baseQuantity: Number(getCellValue(row, qtyIdx)) || 0,
          attritionRate: Number(getCellValue(row, rateIdx)) || 0,
          useQuantity: Number(getCellValue(row, qtyIdx)) || 0,
          useAmount: getRealQuantity(row, saleQty, qtyIdx, rateIdx),
          extCount: 0,
          replaceParentId: '',
          beReplaced: false,
          bomProcess: curProcess,
          bomIndex: getCellValue(row, seqIdx),
          locators: getCellValue(row, locatorIdx),
        })
      } else curProcess = getCellValue(row, 0)
    } else {

      //找到了表头，赋值相关初始化字段
      if (isHeaderRow(row)) {
        headerStart = i;
        seqIdx = row.findIndex(v => !!v && v.includes("序号"))
        codeIdx = row.findIndex(v => !!v && v.includes("料号"))
        nameIdx = row.findIndex(v => !!v && v.includes("品名"))
        modelIdx = row.findIndex(v => !!v && v.includes("规格"))
        uomIdx = row.findIndex(v => !!v && v.includes("单位"))
        qtyIdx = row.findIndex(v => !!v && v.includes("数量"))
        rateIdx = row.findIndex(v => !!v && v.includes("损耗率"))
        remarkIdx = row.findIndex(v => !!v && v.includes("备注"))
        locatorIdx = row.findIndex(v => !!v && v.includes("位置"))
      };
    }
  }
  return {
    list: [...resData],
    header: { ...headerData }
  };
};

export const makeBomData = (flatData: Array<ResReqColType>) => {
  // bomIndex 到数据的映射，便于查找父节点
  const bomIndexMap = new Map<string, ResReqColType>();
  // 最终结果数组
  const resData: Array<ResReqColType> = [];

  // 先建立 bomIndex 映射
  for (const item of flatData) {
    if (item.bomIndex) {
      bomIndexMap.set(`${item.bomProcess}>>>${item.bomIndex}`, item);
    }
  }

  // 遍历数据进行分类
  for (const item of flatData) {
    // 如果没有 bomIndex，直接添加到结果
    if (!item.bomIndex) {
      resData.push(item);
      continue;
    }

    // 检查是否是子节点（bomIndex 包含 '-'）
    let hasParent = item.bomIndex.includes('-');
    if (hasParent) {
      let bomIndex = item.bomIndex.split('-')[0];
      let process = item.bomProcess;
      const parent = bomIndexMap.get(`${process}>>>${bomIndex}`);
      if (parent) {
        parent.reqs.push(item);
      }
    } else {
      // 不是子节点，直接添加到结果
      resData.push(item);
    }
    // const hyphenIndex = item.bomIndex.indexOf('-');
    // if (hyphenIndex !== -1) {
    //   // 是子节点，找到父节点的 bomIndex
    //   const parentBomIndex = item.bomIndex.substring(0, hyphenIndex);

    // } else {
    //   // 不是子节点，直接添加到结果
    //   resData.push(item);
    // }
  }

  return resData;
};